import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  formatDay,
  LICENCE_BODY_LABELS,
  MAX_LICENCE_WARNING_DAYS,
  parseIsoDay,
} from '@wodi/domain';
import type { LicenceStatusView, PractitionerView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
  Section,
} from '@wodi/ui';
import { useState } from 'react';

import {
  practitionerKeys,
  practitionersQuery,
  saveLicenceWarningDays,
} from '../api/practitioners.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PractitionerDialog } from '../practitioners/practitioner-dialog.tsx';
import styles from '../practitioners/practitioners.module.css';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Practitioners (docs/plans/phase1-consultation.md §7.4, P13): each clinician's
// cadre, licensing council, licence number and expiry, with the licence state in words.
// Prints carry these details, and "Finish consultation" warns when a licence has expired
// or has no expiry date.

function day(iso: string | null): string {
  if (iso === null) return 'Not recorded';
  const parsed = parseIsoDay(iso);
  return parsed.ok ? formatDay(parsed.value) : iso;
}

/** The licence state in words, with a shape (DESIGN.md §7: no status colours). */
export function licenceStateWords(licence: LicenceStatusView): string {
  switch (licence.state) {
    case 'no_profile':
      return 'Inactive';
    case 'not_recorded':
      return 'Expiry not recorded';
    case 'expired':
      return 'Expired';
    case 'expiring':
      return licence.daysLeft === 0
        ? 'Expires today'
        : licence.daysLeft === 1
          ? 'Expires tomorrow'
          : `Expires within ${String(licence.daysLeft)} days`;
    case 'valid':
      return 'Valid';
  }
}

function LicenceTag({ licence }: { licence: LicenceStatusView }) {
  return (
    <Tag
      state={
        licence.state === 'valid'
          ? 'done'
          : licence.state === 'expired'
            ? 'failed'
            : licence.state === 'no_profile'
              ? 'neutral'
              : 'attention'
      }
    >
      {licenceStateWords(licence)}
    </Tag>
  );
}

function WarningWindow({ days }: { days: number }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(String(days));
  const parsed = /^\d{1,3}$/.test(value.trim()) ? Number(value.trim()) : Number.NaN;
  const valid = Number.isInteger(parsed) && parsed >= 0 && parsed <= MAX_LICENCE_WARNING_DAYS;
  const save = useMutation({
    mutationFn: () => saveLicenceWarningDays(parsed),
    onSuccess: (list) => {
      queryClient.setQueryData(practitionerKeys.list, list);
      toasts.show({ title: 'Warning period saved' });
    },
  });
  return (
    <Section title="When licences show as expiring">
      <form
        className={styles.actions}
        aria-label="Licence warning period"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) save.mutate();
        }}
      >
        <TextField
          label="Days before expiry"
          description={`Licences show "Expires within … days" this many days ahead. 0 to ${String(MAX_LICENCE_WARNING_DAYS)}.`}
          value={value}
          onChange={setValue}
          {...(valid
            ? {}
            : {
                errorMessage: `Enter a whole number from 0 to ${String(MAX_LICENCE_WARNING_DAYS)}.`,
              })}
        />
        <Button
          type="submit"
          isDisabled={!online || !valid || parsed === days}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Save warning period
        </Button>
      </form>
      {save.isError ? (
        <Notice tone="error">{errorText(save.error, "The period wasn't saved. Try again.")}</Notice>
      ) : null}
    </Section>
  );
}

export function PractitionersPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'practitioners.manage');
  const canSha = session !== null && can(session, 'sha.settings.manage');
  const online = useOnline();
  const list = useQuery({ ...practitionersQuery, enabled: allowed });
  const [dialog, setDialog] = useState<{ editing?: PractitionerView } | null>(null);

  if (!allowed) {
    return (
      <>
        <PageHeader title="Practitioners" />
        <Notice tone="info">
          You don&apos;t have permission to change practitioners&apos; details. Ask an administrator
          if you need it.
        </Notice>
      </>
    );
  }

  return (
    <SettingsPage
      title="Practitioners"
      actions={
        <Button
          variant="primary"
          isDisabled={!online || !list.isSuccess || list.data.candidates.length === 0}
          onPress={() => {
            setDialog({});
          }}
        >
          Add practitioner
        </Button>
      }
      intro={false}
    >
      <div className={styles.page}>
        <p className={styles.intro}>
          Cadre, licensing council, licence number and expiry for each clinician. Prescriptions
          print them, and Finish consultation warns a clinician whose licence has expired or has no
          expiry date, because SHA may reject the claim.{' '}
          {canSha ? (
            <>
              Link practitioners to the national Health Worker Registry in{' '}
              <Link to="/settings/sha">Settings, SHA</Link>.
            </>
          ) : null}
        </p>
        {online ? null : (
          <Notice tone="info">
            You&apos;re offline. The list shows what was last loaded; changes wait until you&apos;re
            back online.
          </Notice>
        )}
        {list.isPending ? <LoadingBlock label="Loading practitioners" rows={4} /> : null}
        {list.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void list.refetch()}>
                Try again
              </Button>
            }
          >
            {errorText(list.error, "Practitioners couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {list.isSuccess ? (
          <Table aria-label="Practitioners">
            <TableHeader>
              <Column isRowHeader>Name</Column>
              <Column>Cadre</Column>
              <Column>Council</Column>
              <Column>Licence number</Column>
              <Column>Expires</Column>
              <Column>Licence</Column>
              <Column>National registry</Column>
              <Column>
                <span className={styles.detail}>Actions</span>
              </Column>
            </TableHeader>
            <TableBody
              items={list.data.items.map((p) => ({ ...p, id: p.userId }))}
              renderEmptyState={() =>
                'No practitioners yet. Add the clinicians who consult, prescribe or sign documents.'
              }
            >
              {(p) => (
                <Row id={p.userId}>
                  <Cell>
                    {p.displayName}
                    {p.status === 'inactive' ? (
                      <span className={styles.detail}> (inactive)</span>
                    ) : null}
                  </Cell>
                  <Cell>{p.cadre?.display ?? 'Not recorded'}</Cell>
                  <Cell>
                    {p.licenceBody === null ? 'Not recorded' : LICENCE_BODY_LABELS[p.licenceBody]}
                  </Cell>
                  <Cell>
                    {p.licenceNumber === null ? (
                      'Not recorded'
                    ) : (
                      <span className={styles.id}>{p.licenceNumber}</span>
                    )}
                  </Cell>
                  <Cell>{day(p.licenceExpiresOn)}</Cell>
                  <Cell>
                    <LicenceTag licence={p.licence} />
                  </Cell>
                  <Cell>{p.registry === null ? 'Not linked' : p.registry.words}</Cell>
                  <Cell>
                    <Button
                      dense
                      isDisabled={!online}
                      aria-label={`Change ${p.displayName}`}
                      onPress={() => {
                        setDialog({ editing: p });
                      }}
                    >
                      Change
                    </Button>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        ) : null}
        {list.isSuccess ? <WarningWindow days={list.data.licenceWarningDays} /> : null}
      </div>
      {dialog !== null && list.isSuccess ? (
        <PractitionerDialog
          list={list.data}
          {...(dialog.editing === undefined ? {} : { editing: dialog.editing })}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
    </SettingsPage>
  );
}
